/**
 * FreeNotesCard — one autosaving textarea ("הערות") for a lead or a clinic.
 *
 * Props:
 *  - value: saved text (string|null)
 *  - updatedAt: ISO string of last save (shown as "נשמר hh:mm")
 *  - onSave(text): async; resolves when persisted, throws on failure
 *  - placeholder
 *
 * Saves 800ms after the last keystroke and on blur. The parent patches the
 * RTK cache optimistically, so `value` catches up without a refetch.
 */

import { useEffect, useRef, useState } from "react";
import { Notepad, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import LoaderLoop from "../../../../components/shared/loader/LoaderLoop";
import "./NivCrmCards.css";

const DEBOUNCE_MS = 800;

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
};

function FreeNotesCard({ value, updatedAt, onSave, placeholder = "הערות חופשיות על הלקוח — נשמר אוטומטית" }) {
  const [draft, setDraft] = useState(value || "");
  const [status, setStatus] = useState("idle"); // idle | dirty | saving | saved | error
  const timerRef = useRef(null);
  const lastSavedRef = useRef(value || "");

  // External value changed (other device / refetch) and we have no local edits
  useEffect(() => {
    if (status === "idle" || status === "saved") {
      setDraft(value || "");
      lastSavedRef.current = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const flush = async (text) => {
    if (text === lastSavedRef.current) {
      setStatus("saved");
      return;
    }
    setStatus("saving");
    try {
      await onSave(text);
      lastSavedRef.current = text;
      setStatus("saved");
    } catch {
      setStatus("error");
    }
  };

  const handleChange = (e) => {
    const next = e.target.value;
    setDraft(next);
    setStatus("dirty");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flush(next), DEBOUNCE_MS);
  };

  const handleBlur = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (status === "dirty") flush(draft);
  };

  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);

  return (
    <section className="nlc-card">
      <header className="nlc-card-head">
        <span className="nlc-card-icon">
          <Notepad size={15} weight="bold" />
        </span>
        <div className="nlc-card-titles">
          <span className="nlc-card-eyebrow">פנימי</span>
          <h3 className="nlc-card-title">הערות</h3>
        </div>
        <span className={`nlc-save-state is-${status}`} aria-live="polite">
          {status === "saving" && (
            <>
              <LoaderLoop size="xs" /> שומר…
            </>
          )}
          {status === "saved" && (
            <>
              <CheckCircle size={12} weight="fill" /> נשמר {formatTime(updatedAt)}
            </>
          )}
          {status === "dirty" && "לא נשמר"}
          {status === "error" && (
            <>
              <WarningCircle size={12} weight="fill" /> השמירה נכשלה
            </>
          )}
          {status === "idle" && updatedAt && `עודכן ${formatTime(updatedAt)}`}
        </span>
      </header>
      <textarea
        className="nlc-textarea"
        value={draft}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        rows={6}
        aria-label="הערות"
      />
    </section>
  );
}

export default FreeNotesCard;
